import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  tagline: 'Textile & dye chemicals',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  landline: '041-2611464',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtNum = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtQty = (n) => {
  const num = parseFloat(n) || 0;
  return num.toFixed(2).replace(/\.?0+$/, '');
};

/**
 * Generate and download a Production Report PDF (K&M style landscape).
 * @param {object} prod - full production object (with consumed[]) from getProductionById
 */
export const generateProductionPdf = (prod) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();   // ~297
  const pageHeight = doc.internal.pageSize.getHeight(); // ~210
  const marginX = 12;
  let y = 14;

  const totalCost = parseFloat(prod.total_cost) || 0;
  const actualOutput = parseFloat(prod.actual_output) || 0;
  const expectedOutput = parseFloat(prod.expected_output) || 0;
  const multiplier = parseFloat(prod.multiplier) || 1;
  const costPerUnit = actualOutput > 0 ? totalCost / actualOutput : 0;
  const totalQtyConsumed = (prod.consumed || []).reduce((s, c) => s + (parseFloat(c.quantity) || 0), 0);

  // ─── Header: logo (left) | title (center) | address (right) ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.rect(marginX, y - 4, 12, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('SC', marginX + 6, y + 3.5, { align: 'center' });

  doc.setFontSize(11);
  doc.text(COMPANY.name, marginX + 16, y + 1);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 120, 120);
  doc.text(COMPANY.tagline, marginX + 16, y + 6);

  // Title center
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(20, 20, 20);
  doc.text('Production Report', pageWidth / 2, y + 2, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('(Un-Audited)', pageWidth / 2, y + 7, { align: 'center' });

  // Right — printed on + address
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(`Printed on: ${new Date().toLocaleString('en-GB')}`, rightX, y - 2, { align: 'right' });
  doc.text(COMPANY.address, rightX, y + 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}  |  ${COMPANY.landline}`, rightX, y + 6, { align: 'right' });

  // ─── Thin divider ───
  y += 12;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, pageWidth - marginX, y);

  // ─── Batch # (left) + Production Date (center) + Status (right) ───
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(26, 60, 110);
  doc.text(`Batch # : ${prod.batch_no}`, marginX, y);

  doc.setTextColor(40, 40, 40);
  doc.text(`Production Date : ${formatDate(prod.production_date)}`, pageWidth / 2, y, { align: 'center' });

  doc.setTextColor(39, 174, 96);
  doc.text(`Status : ${(prod.status || 'Completed').toUpperCase()}`, rightX, y, { align: 'right' });

  // ─── Bold divider before summary ───
  y += 4;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.6);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 6;

  // ─── Summary block (2 columns) ───
  const colW = (pageWidth - marginX * 2) / 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(26, 60, 110);
  doc.text('Production Info', marginX, y);
  doc.text('Output & Cost', marginX + colW, y);
  y += 4;

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 5;

  const drawRow = (label, value, x, valColor) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text(label, x, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...(valColor || [30, 30, 30]));
    doc.text(String(value || '—'), x + 40, y);
  };

  drawRow('Product', `${prod.product_code || ''} — ${prod.product_name || ''}`, marginX);
  drawRow('Expected Output', `${fmtQty(expectedOutput)} ${prod.product_unit || ''}`, marginX + colW);
  y += 5;

  drawRow('Formula Code', prod.formula_code, marginX);
  drawRow('Actual Output', `${fmtQty(actualOutput)} ${prod.product_unit || ''}`, marginX + colW, [39, 174, 96]);
  y += 5;

  drawRow('Multiplier', `${multiplier.toFixed(1)}x`, marginX);
  drawRow('Total Cost', `Rs ${fmtNum(totalCost)}`, marginX + colW, [192, 57, 43]);
  y += 5;

  drawRow('Completed At', formatDateTime(prod.completed_at), marginX);
  drawRow(`Cost per ${prod.product_unit || 'unit'}`, `Rs ${costPerUnit.toFixed(2)}`, marginX + colW, [26, 60, 110]);
  y += 7;

  // ─── Bold divider before table ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(26, 60, 110);
  doc.text('Raw Materials Consumed', marginX, y);
  y += 2;

  // ─── Consumed materials table ───
  const rows = (prod.consumed || []).map(c => [
    c.product_code || '—',
    c.product_name || '—',
    c.source_batch_no || '—',
    fmtQty(c.quantity),
    c.product_unit || '—',
    fmtNum(c.cost),
  ]);

  autoTable(doc, {
    startY: y + 2,
    head: [['Item ID', 'Material', 'From Batch', 'Quantity', 'Unit', 'Cost (Rs)']],
    body: rows.length ? rows : [['—', 'No materials consumed', '—', '—', '—', '—']],
    foot: [[
      '', '', 'Total > > >',
      fmtQty(totalQtyConsumed),
      '',
      fmtNum(totalCost),
    ]],
    theme: 'grid',
    headStyles: {
      fillColor: [232, 232, 232], textColor: [40, 40, 40],
      fontStyle: 'bold', fontSize: 8,
      lineColor: [150, 150, 150], lineWidth: 0.2,
    },
    bodyStyles: { fontSize: 8, textColor: [40, 40, 40], lineColor: [180, 180, 180], lineWidth: 0.15 },
    footStyles: {
      fillColor: [245, 245, 245], textColor: [40, 40, 40],
      fontStyle: 'bold', fontSize: 8,
      lineColor: [150, 150, 150], lineWidth: 0.2,
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 5 && data.cell.raw) {
        data.cell.styles.textColor = [192, 57, 43]; // Cost red
      }
      if (data.section === 'body' && data.column.index === 3) {
        data.cell.styles.textColor = [26, 60, 110]; // Quantity blue
      }
    },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 20, halign: 'center' },
      5: { cellWidth: 32, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 5;

  // ─── Bold divider after table ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, afterY, pageWidth - marginX, afterY);
  afterY += 6;

  // ─── Final totals box (right side) ───
  const totBoxW = 90;
  const totBoxX = pageWidth - marginX - totBoxW;
  const totRows = [
    ['Total Qty Consumed', `${fmtQty(totalQtyConsumed)}`, [40, 40, 40]],
    ['Actual Output', `${fmtQty(actualOutput)} ${prod.product_unit || ''}`, [39, 174, 96]],
    ['Total Cost', `Rs ${fmtNum(totalCost)}`, [192, 57, 43]],
    [`Cost per ${prod.product_unit || 'unit'}`, `Rs ${costPerUnit.toFixed(2)}`, [26, 60, 110]],
  ];
  let ty = afterY;
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.3);
  doc.rect(totBoxX, afterY - 4, totBoxW, totRows.length * 6 + 2);

  doc.setFontSize(8.5);
  totRows.forEach((r, i) => {
    const isLast = i === totRows.length - 1;
    if (isLast) {
      doc.setFillColor(240, 240, 240);
      doc.rect(totBoxX, ty - 3, totBoxW, 6, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(r[0], totBoxX + 2, ty + 1);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...r[2]);
    doc.text(r[1], totBoxX + totBoxW - 2, ty + 1, { align: 'right' });
    ty += 6;
  });

  // ─── Notes (if any) ───
  if (prod.notes) {
    let notesY = ty + 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Notes:', marginX, notesY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(90, 90, 90);
    const lines = doc.splitTextToSize(prod.notes, pageWidth - marginX * 2 - 20);
    doc.text(lines, marginX + 15, notesY);
  }

  // ─── Signatures ───
  let sigY = pageHeight - 22;
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.3);
  const sigW = 60;
  const gap = (pageWidth - marginX * 2 - sigW * 3) / 2;
  const sigs = ['Prepared By', 'Verified By', 'Approved By'];
  sigs.forEach((label, i) => {
    const sx = marginX + i * (sigW + gap);
    doc.line(sx, sigY, sx + sigW, sigY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(label, sx + sigW / 2, sigY + 5, { align: 'center' });
  });

  // ─── Page number ───
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  doc.save(`Production_${prod.batch_no || 'Report'}.pdf`);
};